import { useState, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Plus, Trash2, KeyRound, ShieldCheck, ShieldOff, Search, Crown, MessageSquare, Sprout } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface AdminUser {
  id: number;
  name: string;
  email: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  plan: string;
  createdAt: string;
  lastSeenAt: string | null;
  followUpEmailSentAt: string | null;
  estimateCount: number;
  invoiceCount: number;
  lastSecurityAction: string | null;
  lastSecurityAt: string | null;
}

const SECURITY_ACTION_LABELS: Record<string, string> = {
  login: "Login",
  logout: "Logout",
  login_failed: "Failed login",
  login_locked: "Account locked",
  password_changed: "Password changed",
  email_changed: "Email changed",
  plan_changed: "Plan changed",
  stripe_connected: "Stripe connected",
};

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

const ONLINE_THRESHOLD_MS = 60 * 1000;

async function adminFetch(path: string, opts?: RequestInit) {
  const res = await fetch(`${BASE}/api/admin${path}`, {
    ...opts,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...opts?.headers },
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`);
  return data;
}

export default function AdminUsers() {
  usePageTitle("Admin – Users");
  const { user: currentUser } = useAuth();
  const [, navigate] = useLocation();
  const [now, setNow] = useState(() => Date.now());
  const [messagingId, setMessagingId] = useState<number | null>(null);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 15000);
    return () => window.clearInterval(id);
  }, []);

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const LIMIT = 50;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createEmail, setCreateEmail] = useState("");
  const [createPassword, setCreatePassword] = useState("");
  const [createIsAdmin, setCreateIsAdmin] = useState(false);
  const [createError, setCreateError] = useState("");
  const [creating, setCreating] = useState(false);

  const [deleteUser, setDeleteUser] = useState<AdminUser | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const [resetUser, setResetUser] = useState<AdminUser | null>(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState("");

  const [togglingAdmin, setTogglingAdmin] = useState<number | null>(null);
  const [toggleError, setToggleError] = useState("");

  const [togglingPlan, setTogglingPlan] = useState<number | null>(null);
  const [planError, setPlanError] = useState("");

  const [seedUser, setSeedUser] = useState<AdminUser | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [seedError, setSeedError] = useState("");
  const [seedToast, setSeedToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      const data = await adminFetch(`/users?page=${page}&limit=${LIMIT}`);
      setUsers(data.users);
      setTotal(data.total);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load users");
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    const id = window.setInterval(() => {
      fetchUsers();
    }, 30000);
    return () => window.clearInterval(id);
  }, [fetchUsers]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError("");
    setCreating(true);
    try {
      await adminFetch("/users", {
        method: "POST",
        body: JSON.stringify({ name: createName, email: createEmail, password: createPassword, isAdmin: createIsAdmin }),
      });
      setCreateOpen(false);
      setCreateName("");
      setCreateEmail("");
      setCreatePassword("");
      setCreateIsAdmin(false);
      setPage(1);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    setDeleteError("");
    setDeleting(true);
    try {
      await adminFetch(`/users/${deleteUser.id}`, { method: "DELETE" });
      setDeleteUser(null);
      setPage(1);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetUser) return;
    setResetError("");
    setResetting(true);
    try {
      await adminFetch(`/users/${resetUser.id}`, {
        method: "PATCH",
        body: JSON.stringify({ password: resetPassword }),
      });
      setResetUser(null);
      setResetPassword("");
    } catch (err) {
      setResetError(err instanceof Error ? err.message : "Failed to reset password");
    } finally {
      setResetting(false);
    }
  };

  const handleTogglePlan = async (user: AdminUser) => {
    setTogglingPlan(user.id);
    setPlanError("");
    const newPlan = user.plan === "pro" ? "free" : "pro";
    try {
      const updated = await adminFetch(`/users/${user.id}/plan`, {
        method: "PATCH",
        body: JSON.stringify({ plan: newPlan }),
      });
      setUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, plan: updated.plan } : u));
    } catch (err) {
      setPlanError(err instanceof Error ? err.message : "Failed to update plan");
    } finally {
      setTogglingPlan(null);
    }
  };

  const handleMessage = async (user: AdminUser) => {
    setMessagingId(user.id);
    setToggleError("");
    try {
      const data = await adminFetch("/support/tickets/initiate", {
        method: "POST",
        body: JSON.stringify({ userId: user.id }),
      });
      navigate(`/admin/support?ticketId=${data.ticketId}`);
    } catch (err) {
      setToggleError(err instanceof Error ? err.message : "Failed to open conversation");
      setMessagingId(null);
    }
  };

  const handleSeed = async () => {
    if (!seedUser) return;
    const targetName = seedUser.name;
    setSeedError("");
    setSeeding(true);
    try {
      await adminFetch(`/users/${seedUser.id}/seed`, { method: "POST" });
      setSeedUser(null);
      setSeedToast({ type: "success", message: `Account for ${targetName} has been seeded with demo data.` });
      setTimeout(() => setSeedToast(null), 5000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Seeding failed";
      setSeedUser(null);
      setSeedToast({ type: "error", message: `Failed to seed account for ${targetName}: ${msg}` });
      setTimeout(() => setSeedToast(null), 7000);
    } finally {
      setSeeding(false);
    }
  };

  const handleToggleAdmin = async (user: AdminUser) => {
    setTogglingAdmin(user.id);
    setToggleError("");
    try {
      const updated = await adminFetch(`/users/${user.id}/admin`, {
        method: "PATCH",
        body: JSON.stringify({ isAdmin: !user.isAdmin }),
      });
      setUsers((prev) => prev.map((u) => u.id === user.id ? { ...u, isAdmin: updated.isAdmin } : u));
    } catch (err) {
      setToggleError(err instanceof Error ? err.message : "Failed to update admin status");
    } finally {
      setTogglingAdmin(null);
    }
  };

  const filteredUsers = users.filter((u) => {
    const q = search.toLowerCase();
    return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "200px" }}>
        <Loader2 style={{ width: "1.5rem", height: "1.5rem", animation: "spin 1s linear infinite" }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "1rem", background: "hsl(var(--destructive) / 0.1)", borderRadius: "0.5rem", color: "hsl(var(--destructive))" }}>
        {error}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem", flexWrap: "wrap", gap: "1rem" }}>
        <div>
          <h1 style={{ fontSize: "1.75rem", fontWeight: 700, marginBottom: "0.25rem" }}>User Management</h1>
          <p style={{ color: "hsl(var(--muted-foreground))", fontSize: "0.9375rem" }}>
            {total} registered {total === 1 ? "user" : "users"}
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus style={{ width: "1rem", height: "1rem", marginRight: "0.5rem" }} />
          Create User
        </Button>
      </div>

      {(toggleError || planError) && (
        <div style={{ marginBottom: "1rem", padding: "0.75rem 1rem", background: "hsl(var(--destructive) / 0.1)", borderRadius: "0.5rem", color: "hsl(var(--destructive))", fontSize: "0.875rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {toggleError || planError}
          <button onClick={() => { setToggleError(""); setPlanError(""); }} style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", fontSize: "1rem" }}>×</button>
        </div>
      )}

      {seedToast && (
        <div style={{ marginBottom: "1rem", padding: "0.75rem 1rem", background: seedToast.type === "success" ? "hsl(142 76% 36% / 0.1)" : "hsl(var(--destructive) / 0.1)", borderRadius: "0.5rem", color: seedToast.type === "success" ? "#16a34a" : "hsl(var(--destructive))", fontSize: "0.875rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {seedToast.message}
          <button onClick={() => setSeedToast(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", fontSize: "1rem" }}>×</button>
        </div>
      )}

      <div style={{ marginBottom: "1rem", position: "relative" }}>
        <Search style={{ position: "absolute", left: "0.75rem", top: "50%", transform: "translateY(-50%)", width: "1rem", height: "1rem", color: "hsl(var(--muted-foreground))" }} />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email..."
          style={{ paddingLeft: "2.25rem" }}
        />
      </div>

      <Card>
        <CardContent style={{ padding: 0 }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid hsl(var(--border))" }}>
                  {["Name", "Email", "Created", "Quotes", "Invoices", "Plan", "Follow-up", "Last Security", "Status", "Actions"].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: "0.75rem 1rem", fontSize: "0.8125rem", fontWeight: 600, color: "hsl(var(--muted-foreground))", whiteSpace: "nowrap" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={10} style={{ textAlign: "center", padding: "2rem", color: "hsl(var(--muted-foreground))" }}>
                      No users found
                    </td>
                  </tr>
                )}
                {filteredUsers.map((user) => (
                  <tr
                    key={user.id}
                    style={{ borderBottom: "1px solid hsl(var(--border))", background: user.id === currentUser?.id ? "hsl(var(--muted) / 0.3)" : "transparent" }}
                  >
                    <td style={{ padding: "0.75rem 1rem", fontWeight: 500, fontSize: "0.9375rem", whiteSpace: "nowrap" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                        {user.lastSeenAt && (now - new Date(user.lastSeenAt).getTime()) <= ONLINE_THRESHOLD_MS ? (
                          <span
                            title="Online now"
                            style={{
                              display: "inline-block",
                              width: "0.5rem",
                              height: "0.5rem",
                              borderRadius: "9999px",
                              background: "#10b981",
                              boxShadow: "0 0 0 0 rgba(16, 185, 129, 0.7)",
                              animation: "pulse-online 1.5s infinite",
                            }}
                          />
                        ) : null}
                        {user.name}
                        {user.isSuperAdmin && (
                          <Badge variant="default" style={{ background: "#1d4ed8", fontSize: "0.6875rem", padding: "0.1rem 0.375rem" }}>Super Admin</Badge>
                        )}
                      </span>
                      {user.id === currentUser?.id && (
                        <span style={{ marginLeft: "0.5rem", fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>(you)</span>
                      )}
                    </td>
                    <td style={{ padding: "0.75rem 1rem", fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>
                      {user.email}
                    </td>
                    <td style={{ padding: "0.75rem 1rem", fontSize: "0.875rem", color: "hsl(var(--muted-foreground))", whiteSpace: "nowrap" }}>
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: "0.75rem 1rem", fontSize: "0.875rem", textAlign: "center" }}>
                      {user.estimateCount}
                    </td>
                    <td style={{ padding: "0.75rem 1rem", fontSize: "0.875rem", textAlign: "center" }}>
                      {user.invoiceCount}
                    </td>
                    <td style={{ padding: "0.75rem 1rem" }}>
                      {user.plan === "pro" ? (
                        <Badge variant="default" style={{ background: "#d97706", fontSize: "0.75rem" }}>Pro</Badge>
                      ) : (
                        <Badge variant="secondary" style={{ fontSize: "0.75rem" }}>Free</Badge>
                      )}
                    </td>
                    <td style={{ padding: "0.75rem 1rem", fontSize: "0.875rem", color: "hsl(var(--muted-foreground))", whiteSpace: "nowrap" }}>
                      {user.followUpEmailSentAt
                        ? new Date(user.followUpEmailSentAt).toLocaleDateString()
                        : <span style={{ color: "hsl(var(--muted-foreground) / 0.5)" }}>—</span>}
                    </td>
                    <td style={{ padding: "0.75rem 1rem", fontSize: "0.8125rem", whiteSpace: "nowrap" }}>
                      {user.lastSecurityAction && user.lastSecurityAt ? (
                        <span title={new Date(user.lastSecurityAt).toLocaleString()}>
                          <span style={{ display: "block", color: "hsl(var(--foreground))", fontWeight: 500 }}>
                            {SECURITY_ACTION_LABELS[user.lastSecurityAction] ?? user.lastSecurityAction}
                          </span>
                          <span style={{ color: "hsl(var(--muted-foreground))", fontSize: "0.75rem" }}>
                            {formatRelativeTime(user.lastSecurityAt)}
                          </span>
                        </span>
                      ) : (
                        <span style={{ color: "hsl(var(--muted-foreground) / 0.5)" }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: "0.75rem 1rem" }}>
                      {user.isSuperAdmin ? (
                        <Badge variant="default" style={{ background: "#1d4ed8", fontSize: "0.75rem" }}>Super Admin</Badge>
                      ) : user.isAdmin ? (
                        <Badge variant="default" style={{ background: "#7c3aed", fontSize: "0.75rem" }}>Admin</Badge>
                      ) : (
                        <Badge variant="secondary" style={{ fontSize: "0.75rem" }}>User</Badge>
                      )}
                    </td>
                    <td style={{ padding: "0.75rem 1rem" }}>
                      <div style={{ display: "flex", gap: "0.375rem", alignItems: "center" }}>
                        {!user.isSuperAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Seed account with demo data"
                            onClick={() => { setSeedUser(user); setSeedError(""); }}
                            style={{ height: "2rem", padding: "0 0.5rem", color: "#16a34a" }}
                          >
                            <Sprout style={{ width: "0.875rem", height: "0.875rem" }} />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Message user"
                          onClick={() => handleMessage(user)}
                          disabled={messagingId === user.id || user.id === currentUser?.id}
                          style={{ height: "2rem", padding: "0 0.5rem" }}
                        >
                          {messagingId === user.id
                            ? <Loader2 style={{ width: "0.875rem", height: "0.875rem", animation: "spin 1s linear infinite" }} />
                            : <MessageSquare style={{ width: "0.875rem", height: "0.875rem" }} />
                          }
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Reset password"
                          onClick={() => { setResetUser(user); setResetPassword(""); setResetError(""); }}
                          disabled={user.isSuperAdmin}
                          style={{ height: "2rem", padding: "0 0.5rem" }}
                        >
                          <KeyRound style={{ width: "0.875rem", height: "0.875rem" }} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          title={user.plan === "pro" ? "Downgrade to Free" : "Upgrade to Pro"}
                          onClick={() => handleTogglePlan(user)}
                          disabled={togglingPlan === user.id || user.isSuperAdmin}
                          style={{ height: "2rem", padding: "0 0.5rem", color: user.plan === "pro" ? "#d97706" : undefined }}
                        >
                          {togglingPlan === user.id
                            ? <Loader2 style={{ width: "0.875rem", height: "0.875rem", animation: "spin 1s linear infinite" }} />
                            : <Crown style={{ width: "0.875rem", height: "0.875rem" }} />
                          }
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          title={user.isAdmin ? "Remove admin" : "Make admin"}
                          onClick={() => handleToggleAdmin(user)}
                          disabled={togglingAdmin === user.id || user.id === currentUser?.id || user.isSuperAdmin}
                          style={{ height: "2rem", padding: "0 0.5rem" }}
                        >
                          {togglingAdmin === user.id
                            ? <Loader2 style={{ width: "0.875rem", height: "0.875rem", animation: "spin 1s linear infinite" }} />
                            : user.isAdmin
                              ? <ShieldOff style={{ width: "0.875rem", height: "0.875rem" }} />
                              : <ShieldCheck style={{ width: "0.875rem", height: "0.875rem" }} />
                          }
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Delete user"
                          onClick={() => { setDeleteUser(user); setDeleteError(""); }}
                          disabled={user.id === currentUser?.id || user.isSuperAdmin}
                          style={{ height: "2rem", padding: "0 0.5rem", color: "hsl(var(--destructive))" }}
                        >
                          <Trash2 style={{ width: "0.875rem", height: "0.875rem" }} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {total > LIMIT && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "1rem", fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>
          <span>
            Page {page} of {Math.ceil(total / LIMIT)} — showing {users.length} of {total} users
          </span>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(Math.ceil(total / LIMIT), p + 1))}
              disabled={page >= Math.ceil(total / LIMIT) || loading}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={(open) => { setCreateOpen(open); if (!open) { setCreateError(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>Add a new user account to the platform.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem", padding: "1rem 0" }}>
              <div>
                <Label htmlFor="create-name">Full Name</Label>
                <Input
                  id="create-name"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder="Jane Smith"
                  required
                  style={{ marginTop: "0.375rem" }}
                />
              </div>
              <div>
                <Label htmlFor="create-email">Email</Label>
                <Input
                  id="create-email"
                  type="email"
                  value={createEmail}
                  onChange={(e) => setCreateEmail(e.target.value)}
                  placeholder="jane@example.com"
                  required
                  style={{ marginTop: "0.375rem" }}
                />
              </div>
              <div>
                <Label htmlFor="create-password">Password</Label>
                <Input
                  id="create-password"
                  type="password"
                  value={createPassword}
                  onChange={(e) => setCreatePassword(e.target.value)}
                  placeholder="Min. 8 characters"
                  required
                  minLength={8}
                  style={{ marginTop: "0.375rem" }}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <input
                  id="create-isAdmin"
                  type="checkbox"
                  checked={createIsAdmin}
                  onChange={(e) => setCreateIsAdmin(e.target.checked)}
                  style={{ width: "1rem", height: "1rem", cursor: "pointer" }}
                />
                <Label htmlFor="create-isAdmin" style={{ cursor: "pointer", marginBottom: 0 }}>
                  Grant admin privileges
                </Label>
              </div>
              {createError && (
                <p style={{ color: "hsl(var(--destructive))", fontSize: "0.875rem" }}>{createError}</p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={creating}>
                {creating && <Loader2 style={{ width: "1rem", height: "1rem", marginRight: "0.5rem", animation: "spin 1s linear infinite" }} />}
                Create User
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteUser} onOpenChange={(open) => { if (!open) setDeleteUser(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteUser?.name}</strong> ({deleteUser?.email})? This will permanently remove their account and all associated data.
            </DialogDescription>
          </DialogHeader>
          {deleteError && (
            <p style={{ color: "hsl(var(--destructive))", fontSize: "0.875rem" }}>{deleteError}</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteUser(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting && <Loader2 style={{ width: "1rem", height: "1rem", marginRight: "0.5rem", animation: "spin 1s linear infinite" }} />}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!seedUser} onOpenChange={(open) => { if (!open) { setSeedUser(null); setSeedError(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Seed Account with Demo Data</DialogTitle>
            <DialogDescription>
              This will add realistic demo data to <strong>{seedUser?.name}</strong>&apos;s account, including customers, quotes, invoices, projects, products, and a cost catalog. Existing data will not be removed. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {seedError && (
            <p style={{ color: "hsl(var(--destructive))", fontSize: "0.875rem" }}>{seedError}</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSeedUser(null)} disabled={seeding}>Cancel</Button>
            <Button onClick={handleSeed} disabled={seeding} style={{ background: "#16a34a", color: "#fff" }}>
              {seeding
                ? <><Loader2 style={{ width: "1rem", height: "1rem", marginRight: "0.5rem", animation: "spin 1s linear infinite" }} />Seeding…</>
                : <><Sprout style={{ width: "1rem", height: "1rem", marginRight: "0.5rem" }} />Seed Account</>
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetUser} onOpenChange={(open) => { if (!open) { setResetUser(null); setResetPassword(""); setResetError(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for <strong>{resetUser?.name}</strong>.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPassword}>
            <div style={{ padding: "1rem 0" }}>
              <Label htmlFor="reset-password">New Password</Label>
              <Input
                id="reset-password"
                type="password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                placeholder="Min. 8 characters"
                required
                minLength={8}
                style={{ marginTop: "0.375rem" }}
              />
              {resetError && (
                <p style={{ color: "hsl(var(--destructive))", fontSize: "0.875rem", marginTop: "0.5rem" }}>{resetError}</p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setResetUser(null)}>Cancel</Button>
              <Button type="submit" disabled={resetting}>
                {resetting && <Loader2 style={{ width: "1rem", height: "1rem", marginRight: "0.5rem", animation: "spin 1s linear infinite" }} />}
                Reset Password
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
